/**
 * The post status vocabulary and what each status allows. Asset-free on
 * purpose (`posts.ts` reaches the platform modules, which import images), so
 * the pure helpers built on it can be unit-tested with `tsx`.
 */

export type SocialPostStatus =
  | 'needs_approval'
  | 'scheduled'
  | 'processing'
  | 'published'
  | 'failed'
  | 'needs_attention'
  | 'cancelled';

/**
 * A post the worker may still move: it is worth refreshing, and it is still
 * on its way out. A status this client does not know counts as settled:
 * polling an unknown terminal state forever would be the worse mistake.
 */
export const isUnsettledStatus = (status: string): boolean =>
  status === 'needs_approval' || status === 'scheduled' || status === 'processing';

/** The post reached the platform. */
export const isPublishedStatus = (status: string): boolean => status === 'published';

/** The post will not go out as it is: it failed, it waits on the user, or the user withdrew it. */
export const isStoppedStatus = (status: string): boolean =>
  status === 'failed' || status === 'needs_attention' || status === 'cancelled';

/**
 * Which actions a status allows, per the contract's table. Cancel and edit
 * share one set; retry is for stopped posts only. An unknown status allows
 * nothing: the API would answer 409 anyway.
 */
export const canEditStatus = (status: string): boolean =>
  status === 'needs_approval' ||
  status === 'scheduled' ||
  status === 'failed' ||
  status === 'needs_attention';
export const canCancelStatus = canEditStatus;
export const canRetryStatus = (status: string): boolean =>
  status === 'failed' || status === 'needs_attention';
